import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import analytics from './analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const topics = [
  { key: 'love', icon: '💕', label: 'Aşk & İlişkiler' },
  { key: 'career', icon: '💼', label: 'Kariyer' },
  { key: 'money', icon: '💰', label: 'Para & Finans' },
  { key: 'family', icon: '👨‍👩‍👧‍👦', label: 'Aile' },
  { key: 'motivation', icon: '🔥', label: 'Motivasyon' },
  { key: 'general', icon: '✨', label: 'Genel' },
];

const AICoachTab = ({ user, onCreditUsed }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const chatRef = useRef(null);

  const creditCost = 2;
  // Elite/Pro users have unlimited credits
  const isUnlimited = user.subscription_type === 'elite' || user.subscription_type === 'pro' || user.credits === -1;
  const hasEnoughCredits = isUnlimited || (user.credits || 0) >= creditCost;

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    analytics.aiCoachStarted(selectedTopic || 'general');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API_URL}/api/coach/message`, {
        message: userMsg,
        topic: selectedTopic || 'general',
        history: messages.slice(-10),
        language: 'tr'
      }, { headers: { Authorization: `Bearer ${token}` } });
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      if (onCreditUsed) onCreditUsed();
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: err.response?.data?.detail || 'Bir hata oluştu. Tekrar deneyin.' }]);
    } finally {
      setLoading(false);
    }
  };

  if (!selectedTopic) {
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="tab-content">
        <h2 style={{ textAlign: 'center', color: 'white', marginBottom: '8px' }}>AI Yaşam Koçu</h2>
        <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: '14px', marginBottom: '28px' }}>
          Hangi konuda rehberlik istersiniz?
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
          {topics.map((t) => (
            <button
              key={t.key}
              onClick={() => { setSelectedTopic(t.key); setMessages([{ role: 'assistant', content: `Merhaba! ${t.label} konusunda size yardımcı olmaya hazırım. Sorunuzu yazın...` }]); }}
              data-testid={`coach-topic-${t.key}`}
              style={{
                padding: '24px 16px', background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px',
                cursor: 'pointer', textAlign: 'center', transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(139,92,246,0.15)'; e.currentTarget.style.borderColor = '#8b5cf6'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
            >
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>{t.icon}</div>
              <div style={{ color: 'white', fontSize: '14px', fontWeight: '500' }}>{t.label}</div>
            </button>
          ))}
        </div>
        <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '12px', marginTop: '20px' }}>
          Her mesaj {creditCost} kredi kullanır
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="tab-content" style={{ display: 'flex', flexDirection: 'column', height: '70vh', maxHeight: '600px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <button
          onClick={() => { setSelectedTopic(null); setMessages([]); }}
          style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: '14px' }}
        >
          ← Konu Değiştir
        </button>
        <span style={{ color: '#c084fc', fontSize: '13px' }}>
          {topics.find(t => t.key === selectedTopic)?.icon} {topics.find(t => t.key === selectedTopic)?.label}
        </span>
      </div>

      <div ref={chatRef} data-testid="coach-chat-messages" style={{
        flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px',
        padding: '12px', background: 'rgba(0,0,0,0.2)', borderRadius: '14px', marginBottom: '12px'
      }}>
        {messages.map((msg, i) => (
          <div key={i} style={{
            alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
            maxWidth: '80%', padding: '12px 16px',
            background: msg.role === 'user' ? 'rgba(139,92,246,0.3)' : 'rgba(255,255,255,0.06)',
            borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
            color: 'rgba(255,255,255,0.9)', fontSize: '14px', lineHeight: 1.6, whiteSpace: 'pre-wrap'
          }}>
            {msg.content}
          </div>
        ))}
        {loading && (
          <div style={{ alignSelf: 'flex-start', padding: '12px 16px', background: 'rgba(255,255,255,0.06)', borderRadius: '16px', color: 'rgba(255,255,255,0.4)' }}>
            Düşünüyor...
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '8px' }}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
          placeholder={hasEnoughCredits ? 'Sorunuzu yazın...' : 'Yetersiz kredi'}
          disabled={!hasEnoughCredits || loading}
          data-testid="coach-message-input"
          style={{
            flex: 1, padding: '14px', background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)', borderRadius: '12px',
            color: 'white', fontSize: '15px'
          }}
        />
        <button
          onClick={handleSend}
          disabled={!input.trim() || loading || !hasEnoughCredits}
          data-testid="coach-send-button"
          style={{
            padding: '14px 20px', background: loading ? 'rgba(139,92,246,0.2)' : '#8b5cf6',
            border: 'none', borderRadius: '12px', color: 'white', cursor: 'pointer',
            fontSize: '15px', fontWeight: '600', whiteSpace: 'nowrap'
          }}
        >
          Gönder ({creditCost}💎)
        </button>
      </div>
    </motion.div>
  );
};

export default AICoachTab;
